import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NgbDateAdapter, NgbDateNativeAdapter, NgbTimeAdapter } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { isNil } from 'lodash';
import {
  ButtonComponent,
  CheckboxComponent,
  FormGroupComponent,
  GroupButtonComponent,
  InputLabelComponent,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  NumberComponent,
  RadioComponent,
  SelectComponent,
  TimepickerComponent
} from 'mbs-ui-kit';
import { NgbTimeStringAdapterFormat12 } from 'mbs-ui-kit/form/timepicker/ngb-time-adapters/ngb-time-string-adapter-format-12';
import { BetaFeatureBuildName } from 'mbs-ui-shared@services/beta-request.service';
import { Subject, combineLatest, noop } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Schedule } from 'wizards/common/schedule.class';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { HoursOrMinutesItems } from 'wizards/models/schedule-advanced-models';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { BetaFeatureRequestComponent } from 'mbs-ui-shared@components/beta-feature-request/beta-feature-request.component';
import {
  daysOfWeekEnumArray,
  getDefaultFullSchedule,
  getDefaultIncrementalSchedule,
  getDefaultSyntheticSchedule,
  weekNumberEnumArray
} from 'wizards/steps/schedule-step/NBF/schedule-step.component.utils';

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-schedule-modal-component',
  templateUrl: './schedule-modal.component.html',
  providers: [
    { provide: NgbTimeAdapter, useClass: NgbTimeStringAdapterFormat12 },
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }
  ],
  imports: [
    BetaFeatureRequestComponent,
    ButtonComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    GroupButtonComponent,
    I18NextPipe,
    InputLabelComponent,
    ModalComponent,
    NgbTooltipModule,
    NumberComponent,
    RadioComponent,
    ReactiveFormsModule,
    SelectComponent,
    TimepickerComponent,
    ModalFooterDirective
  ]
})
export class NBFScheduleModalComponent implements OnInit {
  private i18nextService = inject(I18NextService);
  private modalService = inject(ModalService);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public readonly hoursOrMinutesItems = HoursOrMinutesItems;
  public readonly daysOfWeekEnumArray = daysOfWeekEnumArray;
  public readonly weekNumberEnumArray = weekNumberEnumArray;
  public readonly syntheticWeekNumberEnumArray = weekNumberEnumArray.filter((item) => item.value !== WeekNumber['Day Of Month']);
  public readonly daysShort = this.i18nextService.t('shared:daysShort', { returnObjects: true });
  public readonly elementSelectors = {
    id: {
      dailyRadio: 'NBF-Schedule-Modal-Daily-Radio',
      monthlyRadio: 'NBF-Schedule-Modal-Monthly-Radio',
      presetsBlock: 'NBF-Schedule-Modal-presets-block',
      presetWeekdaysButton: 'NBF-Schedule-Modal-presets-weekdays',
      presetEverydayButton: 'NBF-Schedule-Modal-presets-everyday',
      daysBlock: 'NBF-Schedule-Modal-days-block',
      dayId: (i) => `NBF-Schedule-Modal-day-${i}`,
      occurrenceBlock: 'NBF-Schedule-Modal-occurrence-block',
      occurrenceSelect: 'NBF-Schedule-Modal-occurrence-select',
      calendarDayBlock: 'NBF-Schedule-Modal-calendar-day-block',
      daysSelectBlock: 'NBF-Schedule-Modal-days-select-block',
      timeRadioBlock: 'NBF-Schedule-Modal-time-radio-block',
      timeTooltip: 'NBF-Schedule-Modal-time-tooltip',
      timeSpecificBlock: 'NBF-Schedule-Modal-time-specific-block',
      timeSpecificRadio: 'NBF-Schedule-Modal-time-specific-radio',
      timeSpecificTimepicker: 'NBF-Schedule-Modal-time-specific-timepicker',
      timeSeveralBlock1: 'NBF-Schedule-Modal-time-several-block-1',
      timeSeveralBlock2: 'NBF-Schedule-Modal-time-several-block-2',
      timeSeveralRadio: 'NBF-Schedule-Modal-time-several-radio',
      timeSeveralEveryNumber: 'NBF-Schedule-Modal-time-several-every-number',
      timeSeveralPeriod: 'NBF-Schedule-Modal-time-several-period',
      timeSeveralTime1: 'NBF-Schedule-Modal-time-several-time-1',
      timeSeveralTime2: 'NBF-Schedule-Modal-time-several-time-2',
      repeatEveryNumber: 'NBF-Schedule-Modal-repeat-every-number',
      repeatEveryWeeks: 'NBF-Schedule-Modal-repeat-every-weeks',
      repeatEveryMonths: 'NBF-Schedule-Modal-repeat-every-months',
      syntheticSection: 'NBF-Schedule-Modal-synthetic-section',
      syntheticUnavailableBlock: 'NBF-Schedule-Modal-synthetic-unavailable-block',
      syntheticMonthlyNote: 'NBF-Schedule-Modal-synthetic-monthly-note',
      syntheticScheduleBlock: 'NBF-Schedule-Modal-synthetic-schedule-block',
      syntheticOccurrenceBlock: 'NBF-Schedule-Modal-synthetic-occurrence-block',
      syntheticDayOfWeekBlock: 'NBF-Schedule-Modal-synthetic-day-of-week-block',
      syntheticWeeklyRadio: 'NBF-Schedule-Modal-synthetic-weekly-radio',
      syntheticMonthlyRadio: 'NBF-Schedule-Modal-synthetic-monthly-radio',
      syntheticOccurrenceSelect: 'NBF-Schedule-Modal-synthetic-occurrence-select',
      syntheticDaySelect: 'NBF-Schedule-Modal-synthetic-day-select',
      syntheticRunMissedCheckbox: 'NBF-Schedule-Modal-synthetic-run-missed-checkbox',
      saveButton: 'NBF-Additional-Modal-save-button',
      cancelButton: 'NBF-Additional-Modal-cancel-button'
    }
  };

  public form: UntypedFormGroup;
  public title: string;
  public preset: { weekdays: boolean; everyday: boolean } = { weekdays: false, everyday: false };
  public week: boolean[] = [false, false, false, false, false, false, false];

  public isFullEqualIncremental = false;
  public readonly betaFeatureBtnData = { backupBuilds: BetaFeatureBuildName.BackupBuilds, syntheticFull: 'ffi-synthetic-full' };

  private mode: 'simple' | 'advanced' | 'full';
  private confirmedSyntheticDay: number;
  private confirmDayModalActive = false;
  private incrementalSchedule: Schedule;
  private scheduleChanged = new Subject<boolean>();

  get isUnix(): boolean {
    return !!this.baseModal && !!this.baseModal.data && this.baseModal.data.isUnix;
  }

  public get isSimple(): boolean {
    return this.mode === 'simple';
  }

  // Unused in component now
  public get isAdvanced(): boolean {
    return this.mode === 'advanced';
  }

  public get isFull(): boolean {
    return this.mode === 'full';
  }

  public get isDaily(): boolean {
    return this.form?.get('recurringPeriod')?.value === 'Daily';
  }

  public get isMonthly(): boolean {
    return this.form?.get('recurringPeriod')?.value === 'Monthly';
  }

  public get isDayOfMonth(): boolean {
    return this.form?.get('occurrence')?.value === -1;
  }

  public get isCountableDayOfMonth(): boolean {
    return this.form?.get('occurrence')?.value !== null && !this.isDayOfMonth;
  }

  public get canSynthetic(): boolean {
    return !!this.baseModal?.data?.canSynthetic;
  }

  public get showSynthetic(): boolean {
    return this.baseModal?.data?.showSynthetic !== false;
  }

  public get syntheticVersion(): string {
    return this.baseModal?.data?.syntheticVersion;
  }

  public get isSyntheticMonthly(): boolean {
    return this.form?.get('syntheticData')?.get('recurringPeriod')?.value === 'Monthly';
  }

  public get isSpecificTime(): boolean {
    return this.form?.get('dailyFreqPeriodOption')?.value === 'OccursAt';
  }

  ngOnInit() {
    this.initData();
    this.initForm();
    this.fullData();
    this.initSyntheticEvents();
    this.showSyntheticRequiredNotice();
  }

  setFrequency(type: 'Daily' | 'Monthly'): void {
    if (type === 'Monthly') {
      this.form.get('dailyFreqPeriodOption').setValue('OccursAt');

      if (isNil(this.form.value.occurrence)) {
        this.form.get('occurrence').setValue(0);
        this.form.get('dayOfWeek').setValue(3);
      }
    }
  }

  setPreset(type: 'weekdays' | 'everyday'): void {
    if (type === 'weekdays') {
      this.form.get('weeklyDayOfWeek').setValue([false, true, true, true, true, true, false]);
      this.preset = { weekdays: true, everyday: false };
    }

    if (type === 'everyday') {
      this.form.get('weeklyDayOfWeek').setValue([true, true, true, true, true, true, true]);
      this.preset = { weekdays: false, everyday: true };
    }
  }

  setDay(day: number): void {
    this.form.get('weeklyDayOfWeek').setValue(this.week.map((value, i) => (day === i ? !value : value)));
  }

  saveClickHandler(): void {
    if (!this.form.valid) {
      return;
    }

    const save = () => this.baseModal.save({ data: this.form.value });

    if (!this.isSimple || !this.canSynthetic || !this.isDaily) {
      return void save();
    }

    this.confirmSyntheticDay(this.form.get('syntheticData').get('dayOfWeek').value, save);
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }

  dailyPeriodChangeHandler(eventParam): void {
    const event = eventParam as 'OccursEvery' | 'OccursAt';

    if (event === 'OccursEvery' && isNil(this.form.value.occursEveryCount)) {
      this.form.get('occursEveryCount').setValue(1);
    }
  }

  private initData(): void {
    this.mode = this.baseModal?.data?.mode;

    if (this.isFull) {
      const isIncrementalEnabled = this.baseModal?.data?.incrementalSchedule.enabled;
      const isDaily = this.baseModal?.data?.incrementalSchedule.recurringPeriod === 'Daily';

      this.incrementalSchedule = isIncrementalEnabled
        ? new Schedule(
            isDaily
              ? {
                  type: 'Daily',
                  days: this.baseModal?.data?.incrementalSchedule.weeklyDayOfWeek || []
                }
              : {
                  type: 'Monthly',
                  occurrence: this.baseModal?.data?.incrementalSchedule.occurrence,
                  dayOfWeek: this.baseModal?.data?.incrementalSchedule.dayOfWeek,
                  dayOfMonth: this.baseModal?.data?.incrementalSchedule.dayOfMonthCount
                }
          )
        : null;

      this.scheduleChanged.pipe(untilDestroyed(this), debounceTime(100)).subscribe(() => this.detectFullAndIncrementalEquality());
    }
  }

  private fullData(): void {
    const data = this.baseModal?.data?.schedule;

    this.title = this.i18nextService.t(`wizards:schedule.scheduleModal.title.${this.mode}`); // TODO: Test

    if (!data) {
      this.form.reset(data);

      return void this.resetSynthetic();
    }

    if (!data.dayOfMonthCount) {
      data.dayOfMonthCount = 1;
    }

    this.form.reset(data);
    this.resetSynthetic();

    StepsHelpers.updateDaysOfWeek(data.weeklyDayOfWeek, this.form, this.daysOfWeekEnumArray);
  }

  private initForm() {
    this.form = new UntypedFormGroup(this.isFull ? getDefaultFullSchedule() : getDefaultIncrementalSchedule());

    if (this.isSimple) {
      this.form.addControl('syntheticData', new UntypedFormGroup(getDefaultSyntheticSchedule()));
    }

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.week = value.weeklyDayOfWeek;

      this.detectPreset();

      if (this.isFull) {
        this.scheduleChanged.next(true);
      }
    });

    combineLatest([this.form.get('recurringPeriod').valueChanges, this.form.get('weeklyDayOfWeek').valueChanges]).subscribe(() =>
      this.allDaysSelectedValidator()
    );

    this.scheduleChanged.next(true);
  }

  private allDaysSelectedValidator(): void {
    let hasDay;

    if (this.isDaily) {
      hasDay = Array.from(this.form.get('weeklyDayOfWeek').value).some((day) => !!day);
    }

    if (this.isDaily && !hasDay) {
      return void this.form.get('weeklyDayOfWeek').setErrors({ emptyDays: true });
    }

    this.form.get('weeklyDayOfWeek').setErrors(null);
  }

  /**
   * We must compare same params
   * For Daily mode: weeklyDayOfWeek
   * For Monthly mode: occurrence + occurrence params
   * @private
   * @return {null}
   */
  private detectFullAndIncrementalEquality(): void {
    if (!this.isFull || !this.incrementalSchedule) {
      return void delete this.isFullEqualIncremental;
    }

    const isDaily = this.form.get('recurringPeriod').value === 'Daily';
    const currentSchedule = new Schedule(
      isDaily
        ? {
            type: 'Daily',
            days: this.form.get('weeklyDayOfWeek').value
          }
        : {
            type: 'Monthly',
            occurrence: this.form.get('occurrence').value,
            dayOfWeek: this.form.get('dayOfWeek').value,
            dayOfMonth: this.form.get('dayOfMonthCount').value
          }
    );

    if (this.incrementalSchedule.match(currentSchedule)) {
      return void (this.isFullEqualIncremental = true);
    }

    delete this.isFullEqualIncremental;
  }

  private resetSynthetic(): void {
    if (!this.isSimple) {
      return;
    }

    const loaded = this.baseModal?.data?.syntheticSchedule;
    const schedule = loaded?.recurringPeriod ? loaded : new UntypedFormGroup(getDefaultSyntheticSchedule()).value;

    this.form.get('syntheticData').reset(schedule, { emitEvent: false });
    this.confirmedSyntheticDay = this.form.get('syntheticData').get('dayOfWeek').value;
  }

  private initSyntheticEvents(): void {
    if (!this.isSimple) {
      return;
    }

    this.form
      .get('syntheticData')
      .get('dayOfWeek')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((day) => this.confirmSyntheticDay(day));
  }

  private showSyntheticRequiredNotice(): void {
    if (!this.isSimple || !this.canSynthetic || !this.baseModal?.data?.syntheticRequired || !this.isDaily) {
      return;
    }

    this.modalService
      .open(
        {
          header: { title: this.i18nextService.t('wizards:schedule.scheduleModal.synthetic.requiredTitle') },
          footer: { cancelButton: { show: false } }
        },
        this.i18nextService.t('wizards:schedule.scheduleModal.synthetic.requiredText')
      )
      .catch(noop);
  }

  private confirmSyntheticDay(day: number, onConfirm: () => void = noop): void {
    if (this.isDayInBackupSchedule(day)) {
      this.confirmedSyntheticDay = day;

      return void onConfirm();
    }

    // mbs-select writes the control twice per pick: once through its inner [formControl], once through onChange
    if (this.confirmDayModalActive) {
      return;
    }

    const revert = () => this.form.get('syntheticData').get('dayOfWeek').setValue(this.confirmedSyntheticDay, { emitEvent: false });
    const dayLabel = (this.daysOfWeekEnumArray.find((item) => item.value === day) || { label: '' }).label;

    this.confirmDayModalActive = true;

    this.modalService
      .open(
        { header: { title: this.i18nextService.t('wizards:schedule.scheduleModal.synthetic.addDayTitle') } },
        this.i18nextService.t('wizards:schedule.scheduleModal.synthetic.addDayText', { day: dayLabel })
      )
      .then((result) => {
        if (!result) {
          return revert();
        }

        this.addDayToBackupSchedule(day);
        this.confirmedSyntheticDay = day;
        onConfirm();
      })
      .catch(revert)
      .finally(() => (this.confirmDayModalActive = false));
  }

  private isDayInBackupSchedule(day: number): boolean {
    return !!(this.form.get('weeklyDayOfWeek').value || [])[day];
  }

  private addDayToBackupSchedule(day: number): void {
    const days = Array.from(this.form.get('weeklyDayOfWeek').value || []);

    days[day] = true;
    this.form.get('weeklyDayOfWeek').setValue(days);
  }

  private detectPreset(): void {
    let isWeekdays = false;
    let isEveryday = false;
    const summary = (this.form.get('weeklyDayOfWeek').value || []).map((value, i) => (value ? String(i) : '')).join('');

    if (summary === '12345') isWeekdays = true;
    if (summary === '0123456') isEveryday = true;

    this.preset = { weekdays: isEveryday ? false : isWeekdays, everyday: isEveryday };
  }
}
